import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";

import apiPublic from "../../../../../services/apiPublic";
import { formatDateTime } from "../../../../../utils/formatDate";

const finalizerFields = [
  "finalizadora",
  "isFinalizadora",
  "is_finalizadora",
  "ocorrenciaFinalizadora",
  "ocorrencia_finalizadora",
];

const isEnabledValue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "sim" ||
  value === "S";

const isFinalizingOccurrence = (item) =>
  finalizerFields.some((field) => isEnabledValue(item[field]));

// `notaFiscal` é `{ id, chaveNf }` (H2 PO1-2920): o histórico público passou a
// ser consultado pela CHAVE. O `id` fica só para exibição — antes ele era o
// parâmetro da consulta, e como é um inteiro sequencial bastava incrementá-lo
// para ler o histórico de entrega de qualquer cliente.
const ModalOcorrencia = ({ modal, toggle, notaFiscal }) => {
  const [ocorrencias, setOcorrencias] = useState(null);
  const [error, setError] = useState(null);

  const handleOpenImage = useCallback((urlImage) => {
    if (urlImage) {
      window.open(urlImage, "_blank", "noopener,noreferrer");
    }
  }, []);

  useEffect(() => {
    if (!modal) return undefined;

    let cancelled = false;

    async function loadOcorrencias() {
      const chaveNf = notaFiscal && notaFiscal.chaveNf;
      if (!chaveNf) {
        setOcorrencias([]);
        return;
      }

      setOcorrencias(null);
      setError(null);

      try {
        const result = await apiPublic.get("/tracking/ocorrencias", {
          params: {
            chaveNf,
          },
        });

        if (cancelled) return;

        if (result.status === 200) {
          const data = result.data.map((item) => ({
            data_criacao_f: formatDateTime(item.data_criacao),
            ...item,
          }));
          setOcorrencias(data);
        }
      } catch (_) {
        if (!cancelled) {
          setError("Não foi possível carregar o histórico desta entrega.");
          setOcorrencias([]);
        }
      }
    }

    loadOcorrencias();

    return () => {
      cancelled = true;
    };
  }, [modal, notaFiscal]);

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      size="lg"
      modalClassName="modal-align-top public-tracking-modal"
      contentClassName="public-tracking-modal__content"
      fade={false}
    >
      <div className="public-tracking-modal__header">
        <div>
          <span className="public-tracking-eyebrow">Histórico</span>
          <h3>Atualizações da entrega</h3>
          <p>Rastreio Everlog #{(notaFiscal && notaFiscal.id) || "-"}</p>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="public-tracking-modal__close"
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody className="public-tracking-modal__body">
        {ocorrencias === null && !error ? (
          <div className="public-tracking-modal-state">
            <Spinner color="info" />
            <span>Carregando histórico...</span>
          </div>
        ) : error ? (
          <div className="public-tracking-modal-state public-tracking-modal-state--error">
            <i className="tim-icons icon-alert-circle-exc" />
            <span>{error}</span>
          </div>
        ) : ocorrencias.length === 0 ? (
          <div className="public-tracking-modal-state">
            {/* Ícone neutro: o check verde lia como "concluído com sucesso",
                quando a mensagem diz justamente que não há nada registrado.
                Achado no QA da PO1-2818. */}
            <i className="tim-icons icon-notes" />
            <span>
              Nenhuma atualização registrada para esta entrega até agora.
            </span>
          </div>
        ) : (
          <div className="public-tracking-events">
            {ocorrencias.map((item, index) => {
              const isFinalizer = isFinalizingOccurrence(item);

              return (
                <div
                  key={`${item.codigo || "ocorrencia"}-${index}`}
                  className={`public-tracking-event${
                    isFinalizer ? " public-tracking-event--finalizer" : ""
                  }`}
                >
                  <div className="public-tracking-event__marker">
                    <span
                      aria-label={
                        isFinalizer
                          ? "Finalização da entrega"
                          : `Atualização ${index + 1}`
                      }
                      title={
                        isFinalizer
                          ? "Finalização da entrega"
                          : `Atualização ${index + 1}`
                      }
                    >
                      {isFinalizer ? (
                        <i
                          className="tim-icons icon-delivery-fast"
                          aria-hidden="true"
                        />
                      ) : (
                        index + 1
                      )}
                    </span>
                  </div>

                  <div className="public-tracking-event__content">
                    <div className="public-tracking-event__header">
                      {/* PO1-2818: `descricao_amigavel` vem de OcorrenciaRastreio,
                          a mesma configuração por embarcador que o modal de
                          tracking do SAC do Flow já usa. Embarcador sem essa
                          config cai no nome cru da ocorrência (a API já aplica
                          o COALESCE, o fallback aqui é só defensivo). */}
                      <strong>
                        {item.descricao_amigavel ||
                          item.nome ||
                          "Atualização da entrega"}
                      </strong>
                      <span>{item.data_criacao_f || "-"}</span>
                    </div>
                    <p>{item.descricao || "Sem descrição registrada."}</p>
                    <div className="public-tracking-event__footer">
                      <div className="public-tracking-event__meta">
                        <span>Código {item.codigo || "-"}</span>
                        {isFinalizer ? (
                          <span className="public-tracking-event__badge">
                            <i
                              className="tim-icons icon-delivery-fast"
                              aria-hidden="true"
                            />
                            Finalização da entrega
                          </span>
                        ) : null}
                      </div>
                      {item.url_image ? (
                        <Button
                          className="public-tracking-link-button"
                          type="button"
                          onClick={() => handleOpenImage(item.url_image)}
                        >
                          <i className="tim-icons icon-attach-87" />
                          Ver anexo
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ModalOcorrencia;
